<template>
  <ul class="mob-list">
    <li v-for="script in scripts" :key="script.chatId">
      <div class="inr-top">
        <div class="left">
          <div class="icon-box" :class="[script.chatType.toLowerCase()]">
            <img :src="getAssetImageUrl(`common/icons/${script.chatType.toLowerCase()}-icon.svg`)" alt="" />
          </div>
          <div class="tit-wrap">
            <div class="">{{ script.chatName }}</div>
            <span>{{ formatDateTimeByLocale(script.createdAt) }}</span>
          </div>
        </div>
        <div class="right">
          <div v-if="script.status === 'CHAT_IN_PROGRESS'" class="add-info">
            <span>{{ t('label_chat_in_progress') }}</span>
            <img class="icon" :src="getAssetImageUrl('common/icons/ing-icon.svg')" alt="" />
          </div>
          <div v-if="script.status === 'COMPLETED'" class="add-info clear">
            <span>{{ t('label_completed_short') }}</span>
            <IconCheckNew></IconCheckNew>
          </div>
        </div>
      </div>
      <div class="ag">
        <template v-if="titleModalId === script.chatId">
          <input v-model="editingTitle" type="text" class="text-input" />
          <button type="button" class="primary-btn auto-m-btn" @click="saveTitle(script.chatId)">
            {{ t('button_save') }}
          </button>
        </template>
        <template v-else>
          <span>{{ script.title }}</span>
          <button type="button" class="tb-gray-btn auto-m-btn" @click="editTitle(script.chatId, script.title)">
            {{ t('label_change_short') }}
          </button>
        </template>
      </div>

      <div class="btn-wrap">
        <div class="scon-select">
          <select @change="requestScriptState(script.chatId, $event.target.value)">
            <option
              v-for="language in getSortedLanguages(script.languageCode)"
              :key="language.code"
              :value="language.code"
            >
              {{ language.name }}
            </option>
          </select>
        </div>
        <DownloadTableStatusButton :script="script" :language-code="script.languageCode" @update="updateScript" />
      </div>
    </li>
  </ul>
</template>

<script setup lang="ts">
import IconCheckNew from '@/components/icons/IconCheckNew.vue';
import DownloadTableStatusButton from '@/components/shared/composite/DownloadTableStatusButton.vue';
import useChatScriptDetail from '@/composables/domains/chat/useChatScriptDetail';
import { ChatScript } from '@/types/response';
import formatDateTimeByLocale from '@/utils/common/formatDateTimeByLocale';
import { getAssetImageUrl } from '@/utils/getAssetImageUrl';

withDefaults(
  defineProps<{
    scripts?: ChatScript[];
  }>(),
  {
    scripts: () => [],
  }
);

const emit = defineEmits<{ update: [chatId: string, updatedScript: Partial<ChatScript>] }>();

const { t } = useI18n();

const editingTitle = ref<string>('');

const updateScript = (chatId: string, updatedScript: Partial<ChatScript>) => {
  emit('update', chatId, updatedScript);
};
const { titleModalId, getSortedLanguages, requestScriptState, requestChangeScriptTitle } =
  useChatScriptDetail(updateScript);

const editTitle = (chatId: string, title: string) => {
  titleModalId.value = chatId;
  editingTitle.value = title;
};

const saveTitle = (chatId: string) => {
  requestChangeScriptTitle(chatId, editingTitle.value);
  editingTitle.value = '';
  titleModalId.value = '';
};
</script>

<style scoped lang="scss">
.mob-list {
  li {
    padding: 1.25rem 0;
    border-bottom: 1px solid $color-gray-40;
    .inr-top {
      display: flex;
      justify-content: space-between;
      align-items: center;
      .left {
        display: flex;
        align-items: center;
        gap: $size-s;
        .icon-box {
          width: 2.5rem;
          height: 2.5rem;
          border-radius: 0.625rem;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: $size-s;
          &.audience {
            background: $audience-bg;
          }
          &.conference {
            background: $conference-bg;
          }
          &.single {
            background: $single-bg;
          }
          &.general {
            background: $general-bg;
          }
          &.meeting {
            background: $meeting-bg;
          }
          img {
            width: 100%;
            height: 100%;
            object-fit: contain;
          }
        }
        .tit-wrap {
          div {
            color: var(--font-default);
          }
          span {
            display: block;
            font-size: $font-size-xs;
            color: $color-gray-200;
            margin-top: $size-xs;
          }
        }
      }
      .right {
        .add-info {
          display: flex;
          align-items: center;
          gap: $size-xs;
          font-size: $font-size-xs;
        }
        .clear {
          color: var(--primary);
        }
      }
    }
    .ag {
      padding: 0.5rem 0 2rem;
      display: flex;
      gap: 0.5rem;
      align-items: center;
      justify-content: space-between;
      .text-input {
        height: 2.5rem;
        min-height: 2.5rem;
      }
      span {
        font-size: $font-size-psmall;
        line-height: 130%;
        color: var(--font-default);
      }
    }
  }
}
.btn-wrap {
  display: flex;
  gap: $size-s;
  align-items: center;
  width: 100%;
  button {
    flex-shrink: inherit;
  }
  .scon-select {
    width: 80%;
    select {
      height: 2.5rem;
      width: 100%;
    }
  }
}
.tb-gray-btn {
  background: var(--button-background-micinput-on);
  color: var(--font-default);
}
.auto-m-btn {
  height: 2.5rem;
  padding: 0 1rem;
  border-radius: 8px;
  font-size: $font-size-psmall;
}
.date-item {
  position: relative;
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    background: url('@/assets/images/common/icons/calendar-icon.svg') no-repeat center;
    width: 20px;
    height: 20px;
    right: 12px;
  }
  :deep(.date-picker) {
    padding: 0.5rem 2.5rem 0.5rem 0.75rem;
  }
}
</style>
